<button mat-button class="ca-button ca-button-xs ms-auto md:my-auto calculation-base" (click)="openDrawer()">
    <ca-icon icon="settings" class="ca-icon-s"></ca-icon><span>{{ 'RBR.BUTTON.CALCULATION_BASE' | translate }}</span>
</button>

<ca-drawer #drawer overriddenWidth="664px" [bodyBordered]="true">
    <h2 drawer-header class="ca-headline-2 ca-bold m-0">{{ headerText() | translate }}</h2>
    <div drawer-body class="pt-4">
        <form [formGroup]="form" class="calculation-base-form flex flex-col">
            <p class="m-0 calculation-base-helper-text">{{ 'RBR.CALCULATION_BASE.SELECTION_HINT' | translate }}</p>
            <h3 class="ca-headline-3 ca-bold m-0">{{ 'RBR.CALCULATION_BASE.PLAN_HEATING_AND_COOLING' | translate }}</h3>

            <div class="flex items-start gap-3">
                <ca-toggle
                    [value]="form.controls.includeTotalBuilding.value"
                    (valueChanged)="form.controls.includeTotalBuilding.setValue($event)"
                ></ca-toggle>
                <div class="flex flex-col gap-1">
                    <div class="ca-text-lg">{{ 'RBR.CALCULATION_BASE.INCLUDE_TOTAL_BUILDING' | translate }}</div>
                    <div class="ca-text-sm">{{ 'RBR.CALCULATION_BASE.UNASSIGNED_AREA_HELPER_TEXT' | translate }}</div>
                </div>
            </div>

            <div class="calculation-base-input-box p-4">
                <div class="w-full md:w-1/2">
                    <mat-form-field
                        appearance="outline"
                        floatLabel="always"
                        subscriptSizing="dynamic"
                        class="ca-form-field ca-form-field-xs ca-form-field-w-full living-area-field with-label with-error-message"
                    >
                        <mat-label>{{ 'RBR.CALCULATION_BASE.TOTAL_LIVING_AREA' | translate }}</mat-label>
                        <input
                            matInput
                            formControlName="totalLivingArea"
                            [attr.aria-label]="'RBR.CALCULATION_BASE.TOTAL_LIVING_AREA' | translate"
                            placeholder="0"
                            [mask]="mask.pattern"
                            [decimalMarker]="mask.decimalMarker!"
                            [separatorLimit]="mask.separatorLimit!"
                            [suffix]="mask.suffix!"
                        />
                        @if (hasError('totalLivingArea', 'required')) {
                            <mat-error>
                                <ca-form-error-message>
                                    {{ 'RBR.CALCULATION_BASE.ERROR_TOTAL_LIVING_AREA_REQUIRED' | translate }}
                                </ca-form-error-message>
                            </mat-error>
                        }

                        @if (hasError('totalLivingArea', 'invalidTotalLivingArea')) {
                            <mat-error>
                                <ca-form-error-message>
                                    {{ 'RBR.CALCULATION_BASE.ERROR_INVALID_TOTAL_LIVING_AREA' | translate }}
                                </ca-form-error-message>
                            </mat-error>
                        }
                    </mat-form-field>
                </div>
            </div>

            <div class="flex items-start gap-3">
                <ca-toggle
                    [value]="form.controls.setCustomLoadFactors.value"
                    (valueChanged)="form.controls.setCustomLoadFactors.setValue($event)"
                ></ca-toggle>
                <div class="flex flex-col gap-1">
                    <div class="ca-text-lg">{{ 'RBR.CALCULATION_BASE.SET_CUSTOM_LOAD_FACTORS' | translate }}</div>
                    <div class="ca-text-sm">{{ 'RBR.CALCULATION_BASE.CUSTOM_LOAD_FACTORS_HELPER_TEXT' | translate }}</div>
                </div>
            </div>

            @if (form.controls.setCustomLoadFactors.value) {
                <div class="calculation-base-input-box flex flex-col gap-4 p-4 md:flex-row custom-load-factors">
                    <div class="flex flex-col gap-1 flex-1 min-w-0">
                        <mat-form-field
                            appearance="outline"
                            floatLabel="always"
                            subscriptSizing="dynamic"
                            class="ca-form-field ca-form-field-xs ca-form-field-w-full load-factor-field with-label"
                        >
                            <mat-label>{{ 'RBR.CALCULATION_BASE.HEAT_LOAD_FACTOR' | translate }}</mat-label>
                            <input
                                matInput
                                formControlName="customHeatLoadFactor"
                                [attr.aria-label]="'RBR.CALCULATION_BASE.HEAT_LOAD_FACTOR' | translate"
                                placeholder="0"
                                [mask]="loadFactorMask.pattern"
                                [decimalMarker]="loadFactorMask.decimalMarker!"
                                [separatorLimit]="loadFactorMask.separatorLimit!"
                                [suffix]="loadFactorSuffix"
                            />
                        </mat-form-field>
                        <div class="ca-text-sm custom-load-factor-description">
                            {{
                                'RBR.CALCULATION_BASE.CURRENT_CALCULATED_HEAT_LOAD_FACTOR'
                                    | translate: { value: (calculatedHeatLoadFactor() | rbrNumber: '1.1-1') ?? '—' }
                            }}
                        </div>
                    </div>

                    <div class="flex flex-col gap-1 flex-1 min-w-0">
                        <mat-form-field
                            appearance="outline"
                            floatLabel="always"
                            subscriptSizing="dynamic"
                            class="ca-form-field ca-form-field-xs ca-form-field-w-full load-factor-field with-label"
                        >
                            <mat-label>{{ 'RBR.CALCULATION_BASE.COOLING_LOAD_FACTOR' | translate }}</mat-label>
                            <input
                                matInput
                                formControlName="customCoolingLoadFactor"
                                [attr.aria-label]="'RBR.CALCULATION_BASE.COOLING_LOAD_FACTOR' | translate"
                                placeholder="0"
                                [mask]="loadFactorMask.pattern"
                                [decimalMarker]="loadFactorMask.decimalMarker!"
                                [separatorLimit]="loadFactorMask.separatorLimit!"
                                [suffix]="loadFactorSuffix"
                            />
                        </mat-form-field>
                        <div class="ca-text-sm custom-load-factor-description">
                            {{
                                'RBR.CALCULATION_BASE.CURRENT_CALCULATED_COOLING_LOAD_FACTOR'
                                    | translate: { value: (calculatedCoolingLoadFactor() | rbrNumber: '1.1-1') ?? '—' }
                            }}
                        </div>
                    </div>
                </div>
            }
        </form>
    </div>
    <button drawer-footer mat-stroked-button class="ca-button ca-button-m" (click)="onCancel()">
        <span>{{ 'CA.BUTTON.CANCEL' | translate }}</span>
    </button>
    <button drawer-footer mat-flat-button class="ca-button ca-button-m" [disabled]="form.invalid" (click)="onConfirm()">
        <span>{{ 'CA.BUTTON.CONFIRM' | translate }}</span>
    </button>
</ca-drawer>
